import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames/bind';
import styles from './AdminDashboardPage.module.css';
import { ToastContext } from '~/components/context/ToastContext';
import { ModalContext } from '~/components/context/ModalContext';
import { BASE_TRANSACTIONS_URL } from '~/helper/apiService';

const cx = classNames.bind(styles);

function RedeemPoints() {
  const [phone, setPhone] = useState('');
  const [customerPoints, setCustomerPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  useEffect(() => {
    const fetchCustomerPoints = async (page = 1) => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_TRANSACTIONS_URL}get-customer-points?page=${page}&limit=${limit}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok) {
          setCustomerPoints(data.customerPoints || []);
          setCurrentPage(data.pagination.page);
          setTotalPages(data.pagination.totalPages);
          setTotal(data.pagination.total);
        } else {
          showToast(data.error || 'Lỗi khi lấy dữ liệu tổng điểm khách hàng', 'error');
        }
      } catch (err) {
        showToast('Lỗi kết nối đến server', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerPoints(currentPage);
  }, [currentPage]);

  const handleRedeem = async () => {
    try {
      const response = await fetch(`${BASE_TRANSACTIONS_URL}redeem-point`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        showToast(data.message, 'success');
        setPhone('');
        // Refresh customer points after redeeming
        const refreshResponse = await fetch(`${BASE_TRANSACTIONS_URL}get-customer-points?page=${currentPage}&limit=${limit}`, {
          credentials: 'include',
        });
        const refreshData = await refreshResponse.json();
        if (refreshResponse.ok) {
          setCustomerPoints(refreshData.customerPoints || []);
          setCurrentPage(refreshData.pagination.page);
          setTotalPages(refreshData.pagination.totalPages);
          setTotal(refreshData.pagination.total);
        }
      } else {
        showToast(data.error || 'Có lỗi xảy ra', 'error');
        return false; // Keep modal open on error
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
      return false; // Keep modal open on error
    }
  };

  const confirmRedeem = () => {
    if (!phone) {
      showToast('Vui lòng nhập số điện thoại', 'error');
      return;
    }
    showModal({
      title: 'Xác Nhận Đổi Điểm',
      message: `Bạn có chắc chắn muốn đổi điểm cho số điện thoại ${phone}?`,
      onConfirm: handleRedeem,
      onCancel: null, // Uses default hideModal from ModalContext
    });
  };

  const handleCustomerClick = (phoneNumber) => {
    setPhone(phoneNumber);
  };

  const [pageInput, setPageInput] = useState("");

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setPageInput("");
    }
  };

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const page = parseInt(pageInput);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setPageInput("");
    } else {
      showToast(`Vui lòng nhập số trang từ 1 đến ${totalPages}`, "error");
    }
  };

  return (
    <div className={cx('redeem-section')}>
      <div className={cx('search-form')}>
        <input
          type="text"
          placeholder="Nhập số điện thoại"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={cx('input')}
        />
        <button
          onClick={confirmRedeem}
          className={cx('button')}
        >
          Đổi Điểm
        </button>
      </div>
      <div className={cx('customer-points-section')}>
        <h2 className={cx('subtitle')}>Danh Sách Tổng Điểm Khách Hàng</h2>
        {error && (
          <div className={cx('message', 'error')}>
            {error}
          </div>
        )}
        {loading ? (
          <>
            <div className={cx("pagination-info")}>
              <div className={cx("skeleton", "skeleton-text", "skeleton-info")}></div>
            </div>
            <table className={cx('table')}>
              <thead>
                <tr className={cx('tr')}>
                  <th className={cx('th')}>Thời gian</th>
                  <th className={cx('th')}>Số điện thoại</th>
                  <th className={cx('th')}>Tổng điểm</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(10)].map((_, index) => (
                  <tr key={index} className={cx('tr')}>
                    <td className={cx('td')}>
                      <div className={cx("skeleton", "skeleton-text")}></div>
                    </td>
                    <td className={cx('td')}>
                      <div className={cx("skeleton", "skeleton-text")}></div>
                    </td>
                    <td className={cx('td')}>
                      <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : customerPoints.length === 0 ? (
          <div className={cx('no-data')}>Không có dữ liệu tổng điểm khách hàng</div>
        ) : (
          <>
            <div className={cx("pagination-info")}>
              Hiển thị {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, total)} trong tổng số {total} khách hàng
            </div>
            <table className={cx('table')}>
              <thead>
                <tr className={cx('tr')}>
                  <th className={cx('th')}>Thời gian</th>
                  <th className={cx('th')}>Số điện thoại</th>
                  <th className={cx('th')}>Tổng điểm</th>
                </tr>
              </thead>
              <tbody>
                {customerPoints.map((point, index) => (
                  <tr key={index} className={cx('tr')}>
                    <td className={cx('td')}>{point.timestamp}</td>
                    <td
                      className={cx('td', 'clickable')}
                      onClick={() => handleCustomerClick(point.phone)}
                      style={{ cursor: 'pointer', color: '#4caf50', textDecoration: 'underline' }}
                    >
                      {point.phone}
                    </td>
                    <td
                      className={cx('td', {
                        positive: point.totalPoints > 0,
                        negative: point.totalPoints < 0,
                      })}
                    >
                      {point.totalPoints}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className={cx("pagination")}>
                <button
                  className={cx("pagination-button")}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Trước
                </button>
                <span className={cx("pagination-info-text")}>
                  Trang {currentPage} / {totalPages}
                </span>
                <form onSubmit={handlePageInputSubmit} className={cx("pagination-input-form")}>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={pageInput}
                    onChange={handlePageInputChange}
                    placeholder="Trang"
                    className={cx("pagination-input")}
                  />
                  <button type="submit" className={cx("pagination-go-button")}>
                    Đi
                  </button>
                </form>
                <button
                  className={cx("pagination-button")}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Sau
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RedeemPoints;